import { v4 as uuidv4 } from 'uuid';

/**
 * Generic localStorage-backed mock store.
 * Used only by Phase 3 services when mock mode is active.
 */
export function createMockStore<T extends { id: string }>(storageKey: string, seed: T[] = []) {
  function load(): T[] {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? (JSON.parse(raw) as T[]) : [...seed];
    } catch {
      return [...seed];
    }
  }

  function save(items: T[]): void {
    localStorage.setItem(storageKey, JSON.stringify(items));
  }

  return {
    getAll: (): T[] => load(),

    getById: (id: string): T | undefined => load().find((item) => item.id === id),

    create: (input: Omit<T, 'id'>): T => {
      const items = load();
      const newItem = { ...input, id: uuidv4() } as T;
      items.push(newItem);
      save(items);
      return newItem;
    },

    update: (id: string, input: Partial<T>): T => {
      const items = load();
      const idx = items.findIndex((item) => item.id === id);
      if (idx === -1) throw new Error(`Item ${id} not found`);
      items[idx] = { ...items[idx], ...input };
      save(items);
      return items[idx];
    },

    remove: (id: string): void => {
      const items = load().filter((item) => item.id !== id);
      save(items);
    },
  };
}

export function nowISO(): string {
  return new Date().toISOString();
}

export { uuidv4 };
